/**
 * walletUi: the one look and vocabulary for every wallet dashboard surface.
 *
 * The theme (C, S), the money and date formatters, the two-level table cells
 * and the hero stat card used to live inside views/WalletDashboard.js. They
 * moved here (16 Oct 2026) so a tab built as its own component (Money In)
 * renders identically to the tabs still inside the view, without a second
 * copy of any of it. Add to this file; do not restate a formatter in a tab.
 */
import React from "react";

export const C = {
  bg:          "#f4f6fb",
  panel:       "#ffffff",
  header:      "#1a2b4a",
  accent:      "#e63757",
  accentSoft:  "#fff0f3",
  blue:        "#2c7be5",
  blueSoft:    "#eaf2ff",
  teal:        "#00b69b",
  tealSoft:    "#e6faf7",
  orange:      "#f6961e",
  orangeSoft:  "#fff4e6",
  purple:      "#7048e8",
  purpleSoft:  "#f1edff",
  green:       "#00b69b",
  greenSoft:   "#e6faf7",
  red:         "#e63757",
  redSoft:     "#fff0f3",
  yellow:      "#f6961e",
  yellowSoft:  "#fff4e6",
  text:        "#1a2b4a",
  textMid:     "#4a5568",
  textLight:   "#8898aa",
  border:      "#e2e8f0",
  inputBg:     "#f8faff",
  // Standard colour for action buttons (Izi, 26 Sep 2026).
  action:      "#083860",
  shadow:      "0 2px 12px rgba(26,43,74,0.08)",
  shadowHover: "0 4px 20px rgba(26,43,74,0.14)",
  radius:      "10px",
  radiusSm:    "6px",
};

export const S = {
  page:       { background: C.bg, minHeight: "100vh", padding: "0 0 48px 0" },
  hero:       {
    background: `linear-gradient(135deg, ${C.header} 0%, #243b6e 100%)`,
    padding: "28px 32px 24px", marginBottom: 28, borderRadius: C.radius,
    color: "#fff", display: "flex", alignItems: "flex-start",
    justifyContent: "space-between", flexWrap: "wrap", gap: 16,
    boxShadow: "0 4px 24px rgba(26,43,74,0.18)",
  },
  heroTitle:  { fontSize: 22, fontWeight: 700, letterSpacing: "-0.3px", marginBottom: 4, color: "#fff" },
  heroSub:    { fontSize: 13, color: "rgba(255,255,255,0.70)", marginBottom: 0 },
  statCard:   {
    background: C.panel, borderRadius: C.radius, padding: "20px 24px",
    boxShadow: C.shadow, border: `1px solid ${C.border}`, height: "100%",
    display: "flex", flexDirection: "column", justifyContent: "space-between",
  },
  statLabel:  { fontSize: 11, fontWeight: 600, letterSpacing: "0.08em", textTransform: "uppercase", color: C.textLight, marginBottom: 6 },
  statValue:  { fontSize: 28, fontWeight: 700, color: C.text, letterSpacing: "-0.5px" },
  statSub:    { fontSize: 12, color: C.textLight, marginTop: 4 },
  panel:      {
    background: C.panel, borderRadius: C.radius, padding: "24px 28px",
    boxShadow: C.shadow, border: `1px solid ${C.border}`, marginBottom: 24,
  },
  panelTitle: { fontSize: 15, fontWeight: 600, color: C.text, letterSpacing: "-0.2px", marginBottom: 16, display: "flex", alignItems: "center", gap: 8 },
  // Data tables sit on one type scale: 13px values, 11px for headers,
  // badges and the quiet line under a value, 12px for compact buttons.
  // A table that skips S.table inherits the 16px page size and ends up
  // with big values beside 10px captions, which reads as unbalanced.
  // ── Table type scale (the house scale in assets/css/lusuku-overrides.css) ──
  //   cell text   13 (--lk-text)    weight 400, textMid; a primary value 600, text
  //   meta line   11 (--lk-text-xs) weight 400, textLight
  //   headers and labels 11 uppercase, weight 600
  // No 700 inside a table, no size off the scale, and a monospace reference
  // takes the cell size rather than its own (Izi, 26 Sep and 25 Oct 2026).
  table:      { width: "100%", borderCollapse: "collapse", fontSize: 13, lineHeight: 1.45, fontVariantNumeric: "tabular-nums" },
  th:         { padding: "10px 14px", textAlign: "left", fontSize: 11, fontWeight: 600, letterSpacing: "0.08em", textTransform: "uppercase", color: C.textLight, borderBottom: `2px solid ${C.border}`, whiteSpace: "nowrap" },
  td:         { padding: "12px 14px", borderBottom: `1px solid ${C.border}`, color: C.textMid, verticalAlign: "middle", fontSize: 13 },
  cellStrong: { fontWeight: 600, color: C.text },
  cellMeta:   { fontSize: 11, lineHeight: 1.4, color: C.textLight, marginTop: 3, fontWeight: 400 },
  cellNum:    { textAlign: "right", whiteSpace: "nowrap", fontVariantNumeric: "tabular-nums" },
  cellMono:   { fontFamily: "ui-monospace, SFMono-Regular, Menlo, Consolas, monospace", fontSize: "inherit", whiteSpace: "nowrap" },
  // Dark-header grid (Disbursements, Manual payout history). Rows are split
  // by a hairline only: no vertical rules between columns.
  gridTh:     (align = "left") => ({
    padding: "11px 14px", textAlign: align, fontSize: 11, fontWeight: 600, letterSpacing: "0.07em",
    textTransform: "uppercase", color: "#c8d6f0", background: C.header, whiteSpace: "nowrap",
    borderBottom: "none", position: "sticky", top: 0, zIndex: 2,
  }),
  gridTd:     (background) => ({
    padding: "11px 14px", borderBottom: `1px solid ${C.border}`, verticalAlign: "middle", background, transition: "background 0.12s",
    fontSize: 13, color: C.textMid,
  }),
  // "primary" and "action" are the same navy: #083860 is THE button colour
  // on the dashboard (Izi, 27 Sep 2026); the mid-blue stays for links and
  // chart tones, never for a control.
  btn:        (variant = "primary") => ({
    background: variant === "primary" || variant === "action" ? C.action : variant === "danger" ? C.red : variant === "teal" ? C.teal : "#edf2f7",
    color: variant === "ghost" ? C.textMid : "#fff",
    border: "none", borderRadius: C.radiusSm, padding: "8px 16px",
    fontSize: 13, fontWeight: 600, cursor: "pointer",
    display: "inline-flex", alignItems: "center", gap: 6,
    transition: "opacity 0.15s",
  }),
  btnSm:      (variant = "ghost") => ({ ...S.btn(variant), padding: "5px 10px", fontSize: 12, lineHeight: 1.25, whiteSpace: "nowrap", justifyContent: "center" }),
  // A status filter chip. It is a BUTTON, so it is filled in the action colour
  // like every other control (Izi, 13 Oct 2026): the selected one solid, the
  // rest a navy tint, never the grey ghost that read as a label beside the
  // tinted status labels in the table.
  filterBtn:  (active) => ({
    border: "none", borderRadius: C.radiusSm, padding: "6px 12px",
    fontSize: 12, fontWeight: 600, lineHeight: 1.25, cursor: "pointer", whiteSpace: "nowrap",
    background: active ? C.action : "rgba(8, 56, 96, 0.10)",
    color: active ? "#fff" : C.action,
    transition: "background 0.15s",
  }),
  input:      {
    background: C.inputBg, border: `1px solid ${C.border}`, borderRadius: C.radiusSm,
    padding: "8px 12px", fontSize: 13, color: C.text, width: "100%", outline: "none",
  },
  // A status LABEL. Tinted text on a soft tint, no border, no shadow: it must
  // not be mistaken for a button, which on this dashboard is a filled navy
  // rectangle (see filterBtn and components/ui/AppButton).
  badge:      (color) => ({
    display: "inline-flex", alignItems: "center", gap: 4, padding: "3px 10px",
    borderRadius: 20, fontSize: 11, fontWeight: 600, letterSpacing: "0.04em",
    whiteSpace: "nowrap",
    background: color === "green" ? C.tealSoft : color === "red" ? C.accentSoft : color === "yellow" ? C.orangeSoft : color === "purple" ? C.purpleSoft : color === "neutral" ? "transparent" : C.blueSoft,
    color: color === "green" ? C.teal : color === "red" ? C.red : color === "yellow" ? C.orange : color === "purple" ? C.purple : color === "neutral" ? C.textLight : C.blue,
    // "neutral" is an outlined, quiet state (e.g. not set up) so it never
    // reads as one of the grey ghost buttons beside it.
    boxShadow: color === "neutral" ? `inset 0 0 0 1px ${C.border}` : undefined,
  }),
};

// MTN's charge on a payout of `amount`, read off the band table the wallet
// service serves (GET /api/disbursement/admin/fee-bands). The dashboard never
// carries its own copy of the bands: two hand-copied versions held MTN's
// cash-out charges for seven months while MTN was charging a third of that.
// Until the bands have loaded there is no estimate (null), not a wrong one.
export const mtnFeeForAmount = (bands, amount) => {
  const n = Number(amount);
  if (!Array.isArray(bands) || bands.length === 0) return null;
  if (!n || n < 500) return 0;
  const band = bands.find((b) => n >= b.min && n <= b.max);
  return band ? band.fee : bands[bands.length - 1].fee;
};

export const fmtUGX = (n) => `UGX ${Number(n || 0).toLocaleString()}`;
// A money cell in a table whose header already says UGX (Izi, 25 Oct 2026).
export const fmtNum = (n) => Number(n || 0).toLocaleString();
export const fmtDate = (d) => d ? new Date(d).toLocaleString("en-UG", { dateStyle: "medium", timeStyle: "short" }) : "—";
export const fmtDay = (d) => new Date(d).toLocaleDateString("en-UG", { dateStyle: "medium" });
export const fmtClock = (d) => new Date(d).toLocaleTimeString("en-UG", { timeStyle: "short" });
export const truncate = (s, n = 16) => s ? (s.length > n ? s.slice(0, n) + "…" : s) : "—";

// A data-table value with an optional quieter line under it: the type
// under a vendor name, the update date or TIN under a status badge.
// Every column shares this shape so a row reads at the same two levels.
export const CellStack = ({ children, meta, metaColor, strong, style }) => (
  <div style={style}>
    <div style={strong ? S.cellStrong : undefined}>{children}</div>
    {meta ? <div style={metaColor ? { ...S.cellMeta, color: metaColor } : S.cellMeta}>{meta}</div> : null}
  </div>
);

// Vendor name over "Wholesaler · UG", identical in every commission table.
export const fmtVendorType = (type) => (type ? type.charAt(0) + type.slice(1).toLowerCase() : "");
export const VendorCell = ({ name, type, country }) => (
  <CellStack strong style={{ minWidth: 130 }} meta={[fmtVendorType(type), country].filter(Boolean).join(" · ")}>
    {name}
  </CellStack>
);

// A timestamp in a table cell: the date, with the time as its quiet line.
export const DateTimeCell = ({ value }) => (value ? (
  <CellStack meta={fmtClock(value)}><span style={{ whiteSpace: "nowrap" }}>{fmtDay(value)}</span></CellStack>
) : <span style={{ color: C.textLight }}>—</span>);

// A period in a table cell: the start date, with "to <end>" as its quiet line.
export const PeriodCell = ({ start, end }) => (start || end ? (
  <CellStack meta={start && end ? <span style={{ whiteSpace: "nowrap" }}>to {fmtDay(end)}</span> : null}>
    <span style={{ whiteSpace: "nowrap" }}>{fmtDay(start || end)}</span>
  </CellStack>
) : <span style={{ color: C.textLight }}>—</span>);


// ── Date ranges (local YYYY-MM-DD pairs, inclusive) ──────────────────────────
export const localDateStr = (d) => {
  const x = d instanceof Date ? d : new Date(d);
  return `${x.getFullYear()}-${String(x.getMonth() + 1).padStart(2, "0")}-${String(x.getDate()).padStart(2, "0")}`;
};
// Quick ranges as local YYYY-MM-DD pairs (inclusive).
export const quickRange = (key) => {
  const today = new Date();
  const y = today.getFullYear(), m = today.getMonth(), d = today.getDate();
  switch (key) {
    case "today":     return { from: localDateStr(today), to: localDateStr(today) };
    case "yesterday": { const t = new Date(y, m, d - 1); return { from: localDateStr(t), to: localDateStr(t) }; }
    case "week": {    // Monday → today
      const dow = (today.getDay() + 6) % 7;
      return { from: localDateStr(new Date(y, m, d - dow)), to: localDateStr(today) };
    }
    case "lastMonth": return { from: localDateStr(new Date(y, m - 1, 1)), to: localDateStr(new Date(y, m, 0)) };
    case "month":
    default:          return { from: localDateStr(new Date(y, m, 1)), to: localDateStr(today) };
  }
};
export const rangeDays = (range) => {
  const a = Date.parse(`${range.from}T00:00:00`), b = Date.parse(`${range.to}T00:00:00`);
  if (!Number.isFinite(a) || !Number.isFinite(b)) return 0;
  return Math.round((b - a) / 86400000) + 1;
};
export const autoBucket = (range) => { const n = rangeDays(range); return n <= 45 ? "day" : n <= 200 ? "week" : "month"; };
// Send whole local days as ISO instants so the wallet's createdAt filter is unambiguous.
export const rangeToIso = (range) => ({
  from: new Date(`${range.from}T00:00:00`).toISOString(),
  to:   new Date(`${range.to}T23:59:59.999`).toISOString(),
});

// A loading placeholder the shape of the value that will arrive.
export const Skeleton = ({ width = 120, height = 14, style }) => (
  <span className="lk-skeleton" aria-hidden="true" style={{ width, height, ...style }} />
);

// Placeholder rows for a table that is still loading: the same number of
// cells as the real rows, so the layout does not jump when data lands.
export const SkeletonRows = ({ columns, rows = 3, tdStyle }) => (
  <>
    {Array.from({ length: rows }).map((_, r) => (
      <tr key={`skeleton-${r}`}>
        {Array.from({ length: columns }).map((__, c) => (
          <td key={c} style={tdStyle || S.td}><Skeleton width={c === 0 ? 110 : 70} height={12} /></td>
        ))}
      </tr>
    ))}
  </>
);

// A stat card. Given onClick it is a control: the whole card opens the
// detail the figure summarises (every dashboard stat card opens what it
// counts, Izi 12 Oct 2026), and it says so with a filled navy "hint" chip,
// because on this dashboard a clickable thing is a filled navy shape.
export function StatCard({ icon, label, value, sub, iconBg, onClick, hint, loading = false }) {
  const clickable = typeof onClick === "function";
  return (
    <div
      role={clickable ? "button" : undefined}
      tabIndex={clickable ? 0 : undefined}
      onClick={onClick}
      onKeyDown={clickable ? (e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onClick(e); } } : undefined}
      title={clickable ? hint : undefined}
      style={{ ...S.statCard, cursor: clickable ? "pointer" : undefined, transition: "box-shadow 0.15s, transform 0.15s" }}
      onMouseEnter={clickable ? (e) => { e.currentTarget.style.boxShadow = C.shadowHover; } : undefined}
      onMouseLeave={clickable ? (e) => { e.currentTarget.style.boxShadow = C.shadow; } : undefined}
    >
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between" }}>
        <div>
          <div style={S.statLabel}>{label}</div>
          <div style={S.statValue}>{loading ? <Skeleton width={150} height={26} /> : value}</div>
          {loading ? <div style={S.statSub}><Skeleton width={190} height={11} /></div> : (sub && <div style={S.statSub}>{sub}</div>)}
        </div>
        <div style={{ background: iconBg || C.blueSoft, borderRadius: 10, padding: 10, display: "flex", alignItems: "center", justifyContent: "center" }}>
          {icon}
        </div>
      </div>
      {clickable && hint && (
        <div style={{ marginTop: 12 }}>
          <span style={{ display: "inline-flex", alignItems: "center", gap: 4, background: C.action, color: "#fff", borderRadius: C.radiusSm, padding: "3px 9px", fontSize: 11, fontWeight: 600, lineHeight: 1.3 }}>
            {hint} <span aria-hidden="true">&rsaquo;</span>
          </span>
        </div>
      )}
    </div>
  );
}

